import { Box, Tab, Tabs } from '@mui/material';
import React, { useEffect, useState } from 'react';

// COMPONENTS
import GlobalList from './GlobalList';
import PersonalList from './PersonalList';
import SingleView from './SingleView';

// REQUESTS
import useTemplateRequest from '../../requests/TemplateRequest';

// MESSAGES
import TemplateFormattedMessage from '../../messages/websms/TemplateFormattedMessages';

const Templates = ({ customer, errorHandler, handleTemplateSelect }) => {
  const [stv, setStv] = useState(false);
  const [tab, setTab] = useState('Global');
  const [data, setData] = useState({ global: [], personal: [] });
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [saveDisabled, setSaveDisabled] = useState(false);
  const { createTemplate, deleteTemplate, editTemplate, getTemplates } =
    useTemplateRequest();

  useEffect(() => {
    if (customer && customer !== null) {
      const fetchData = async () => {
        try {
          let data = await getTemplates(errorHandler);

          let sortedGlobal = sortList(data.global);
          let sortedPersonal = sortList(data.personal);

          setData({ global: sortedGlobal, personal: sortedPersonal });
          setLoading(false);
        } catch (e) {
          setError(true);
          setErrorMessage(e.message);
          setLoading(false);
        }
      };

      fetchData();
    }
  }, [customer, errorHandler]);

  const sortList = (data) => {
    return data.sort((a, b) => a.template_name.localeCompare(b.template_name));
  };

  const showSingleViewCallback = () => {
    setStv(true);
  };

  const handleBackClick = () => {
    setStv(false);
    setSelected(null);
  };

  const handleSelect = (template) => {
    if (handleTemplateSelect !== undefined) {
      handleTemplateSelect(template);
    } else {
      setStv(true);
      setSelected(template);
    }
  };

  const handleSaveClick = async (templateName, templateContent) => {
    if (saveDisabled) return;

    setSaveDisabled(true);
    try {
      await createTemplate(templateName, templateContent, tab, errorHandler);

      // Refresh data
      let data = await getTemplates(errorHandler);
      setData(data);
      setSelected(null);
    } catch (e) {
      errorHandler(e);
    } finally {
      setSaveDisabled(false);
    }
  };

  const handleEditClick = async (templateName, templateContent) => {
    let updates = { template_type: tab };
    if (templateName) updates.template_name = templateName;
    if (templateContent) updates.template_text = templateContent;

    try {
      await editTemplate(selected.template_sms_id, updates, errorHandler);

      let data = await getTemplates(errorHandler);
      setData(data);
    } catch (e) {
      errorHandler(e);
    }
  };

  const handleDeleteClick = async (templateId) => {
    try {
      await deleteTemplate(templateId, tab, errorHandler);

      // Refresh data
      let data = await getTemplates(errorHandler);
      setData(data);
      setSelected(null);
    } catch (e) {
      errorHandler(e);
    }
  };

  const handlePageReset = () => {
    setSelected(null);
    setTab('Global');
    setStv(false);
  };

  const generateBody = () => {
    if (error) return <div>Error: {errorMessage}</div>;
    if (loading) return <div>{TemplateFormattedMessage.loadingText}...</div>;

    const globalTemplate = data.global;
    const personalTemplate = data.personal;

    if (stv) {
      return (
        <SingleView
          tab={tab}
          selected={selected}
          callBack={handleBackClick}
          handleSave={handleSaveClick}
          saveDisabled={saveDisabled}
          handleEdit={handleEditClick}
          handleDelete={handleDeleteClick}
          errorHandler={errorHandler}
        />
      );
    }

    return (
      <Box>
        <Tabs
          value={tab}
          onChange={(event, newValue) => setTab(newValue)}
          sx={{
            borderBottom: '2px solid',
            borderColor: 'secondary.light',
            minHeight: 38,
          }}
        >
          <Tab
            label={TemplateFormattedMessage.globalTemplateTitle}
            value="Global"
            sx={{ paddingTop: 0.5 }}
          />
          <Tab
            label={TemplateFormattedMessage.personalTemplateTitle}
            value="Personal"
            sx={{ paddingTop: 0.5 }}
          />
        </Tabs>
        {tab === 'Global' && (
          <Box sx={{ marginY: 2 }}>
            <GlobalList
              list={globalTemplate}
              callBack={showSingleViewCallback}
              selectOnly={true}
              handleSelect={handleSelect}
              errorHandler={errorHandler}
            />
          </Box>
        )}

        {tab === 'Personal' && (
          <Box sx={{ marginY: 2 }}>
            <PersonalList
              list={personalTemplate}
              callBack={showSingleViewCallback}
              selectOnly={true}
              handleSelect={handleSelect}
              errorHandler={errorHandler}
            />
          </Box>
        )}
      </Box>
    );
  };

  return <Box>{generateBody()}</Box>;
};

export default Templates;
